// SumSubInstructions.jsx
import React, { useState, useEffect, useCallback, useContext } from "react";
import { Container, Row, Col } from "reactstrap";
import axios from "axios";
import SumsubWebSdk from "@sumsub/websdk-react";
import { JobContext } from "../../contexts/job";
import { WORKSHOP_BASE_URL } from "../../utils";
import { getKycFlow } from "../../data/kycFlows";

// What the contributor sees once they've finished their part of the KYC flow.
// The final approve/reject decision is authoritative server-side (Sumsub
// webhook -> sumsub_status); in-session we usually only know they submitted,
// so the default outcome is "processing" rather than a premature "approved".
const RESULT_MESSAGES = {
  approved: {
    title: "Identity verified",
    body: "Thanks — your identity has been verified and your payment will be processed.",
  },
  declined: {
    title: "Verification unsuccessful",
    body: "We weren't able to verify your identity, so we can't process payment for this job. If you think this is a mistake, please contact support.",
  },
  processing: {
    title: "Verification received",
    body: "Thanks — we've received your verification and it's being reviewed. This can take a little while; your payment will be released once your identity is approved.",
  },
};

export default function SumSubInstructions({ cid, flow, lang }) {
  const { goNext } = useContext(JobContext);
  const [accessToken, setAccessToken] = useState(null);
  // Whether the applicant has finished submitting their part of the flow, and
  // the best outcome we can infer in-session: processing | approved | declined.
  const [kycSubmitted, setKycSubmitted] = useState(false);
  const [kycOutcome, setKycOutcome] = useState("processing");

  // The flow config (SumSub level + instruction copy) is resolved from the
  // block's `flow` selector. Note this is separate from the block's data_type,
  // which must stay 'sumsub_data_check' for the workshop post-processing
  // pipeline. Unknown/missing flow falls back to the default.
  const flowConfig = getKycFlow(flow);

  const fetchAccessToken = useCallback(async () => {
    try {
      // The verification level comes from this flow's config; the workshop
      // endpoint mints a token for whatever level we send.
      const resp = await axios.post(`${WORKSHOP_BASE_URL}/get-sumsub-token`, {
        external_user_id: cid,
        level_name: flowConfig.level,
      });
      const token = resp.data.token;
      // Keep state in sync for the initial render AND return the token so the
      // WebSDK's expirationHandler can refresh the session when it expires.
      setAccessToken(token);
      return token;
    } catch (err) {
      console.error(
        "Failed to fetch SumSub access token:",
        err.response?.data || err
      );
      // Re-throw so the SDK's expirationHandler rejects rather than resolving
      // to undefined, which would silently leave the session unrefreshed.
      throw err;
    }
  }, [cid, flowConfig.level]);

  useEffect(() => {
    fetchAccessToken();
  }, []);

  const handleMessage = (type, payload) => {
    if (type === "idCheck.onApplicantSubmitted") {
      // Documents submitted — the applicant's part is done. Most reviews
      // complete asynchronously after this, so default to "processing".
      setKycSubmitted(true);
    } else if (type === "idCheck.onApplicantStatusChanged") {
      // Refine the message only when a final result is actually available.
      const answer = payload?.reviewResult?.reviewAnswer;
      const rejectType = payload?.reviewResult?.reviewRejectType;
      if (answer === "GREEN") {
        setKycOutcome("approved");
        setKycSubmitted(true);
      } else if (answer === "RED" && rejectType === "FINAL") {
        setKycOutcome("declined");
        setKycSubmitted(true);
      } else if (answer === "RED" && rejectType === "RETRY") {
        // Resubmission requested — keep the SDK open so the user can retry.
        setKycOutcome("processing");
        setKycSubmitted(false);
      }
      // pending / onHold / awaiting* statuses leave the default "processing".
    }
  };

  const result = RESULT_MESSAGES[kycOutcome] || RESULT_MESSAGES.processing;

  return (
    <Container fluid="sm" className="retro-instructions">
      <Row>
        <Col>
          {kycSubmitted ? (
            <div className="instructions-summary">
              <h2>{result.title}</h2>
              <p>{result.body}</p>
              <div className="action-button">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={goNext}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="instructions-summary">
                <h2>{flowConfig.title}</h2>
                <p>{flowConfig.intro}</p>
              </div>

              <div className="steps">
                {flowConfig.steps.map((step, i) => (
                  <div className="step" key={i}>
                    <div className="step-number">{i + 1}</div>
                    <div className="step-description">
                      <p>{step}</p>
                    </div>
                  </div>
                ))}

                {!accessToken ? (
                  <p>Loading KYC flow…</p>
                ) : (
                  <div style={{ height: 600, marginTop: 16 }}>
                    <SumsubWebSdk
                      accessToken={accessToken}
                      expirationHandler={fetchAccessToken}
                      config={{ lang: lang || "en" }}
                      options={{ adaptIframeHeight: true }}
                      onMessage={handleMessage}
                      onError={(error) => console.error("SDK error:", error)}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
